import {
  LoaderFunction,
  Outlet,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get } from "api";
import ShopCart from "atoms/icon/icons/cart.svg";
import ShopBag from "atoms/icon/icons/ic_shop_nav_ol.svg";
import StarIcon from "atoms/icon/icons/star.svg";
import {
  filterItemsByProducts,
  generateShopUrl,
  normalizeItemName,
} from "modules/retailProducts";
import { AccordionList } from "molecules/accordion-list/AccordionList";
import { queryClient } from "pages/Root";
import { loadIfFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";
import {
  hasBestSellers,
  hasFeatured,
  hasNewArrivals,
  hasProducts,
} from "utils/retailProducts.helpers";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const loader = async () => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const products = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/product-listings"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/product-listings", {
        platformId: platformData.id,
      });
    }
  );

  const productTypes = queryClient.fetchQuery(
    ["/retailproducts/product-types"],
    async () => {
      return await get("/retailproducts/product-types", {
        languageCode: 0,
      });
    }
  );

  const brands = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/default/brands"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/default/brands", {
        platformId: platformData.id,
      });
    }
  );

  const shopData = await get("/shop");
  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );

  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  return waitObj({
    theme,
    shopData,
    products,
    productTypes,
    brands,
    user,
  });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const shopProductsLoader: LoaderFunction =
  loadIfFlag("selling-products")(loader);

const ShopProductsPage = () => {
  const { theme, shopData, products, productTypes, brands } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();

  const headerImage = theme.resources?.find(
    (resource) => resource.key === "retail-products-header"
  )?.url;

  const productConcernIds = products.flatMap((product) => product.concernIds);
  const productAreaIds = products.flatMap((product) => product.areaIds);
  const productBrandIds = products.flatMap((product) => product.brandId);
  const productTypeIds = products.flatMap((product) => product.productTypeIds);

  const filteredConcerns = filterItemsByProducts(
    shopData.concerns ?? [],
    productConcernIds
  );

  const filteredAreas = filterItemsByProducts(
    shopData.areas ?? [],
    productAreaIds
  );

  const filteredBrands = filterItemsByProducts(brands, productBrandIds);
  const filteredProductTypes = filterItemsByProducts(
    productTypes,
    productTypeIds
  );

  const normalizedConcerns = filteredConcerns.map(normalizeItemName);
  const normalizedAreas = filteredAreas.map(normalizeItemName);

  const createExpandableSection = (
    title: string,
    items: Array<{ id: string; name: string | { text: string } }>,
    view: string,
    paramKey: string
  ) => ({
    title,
    titleClassName: "text-bold2",
    startIcon: ShopBag,
    hasExpandableContent: true,
    children: (
      <div className="flex flex-col gap-4 pb-4 pl-8">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() =>
              navigate(generateShopUrl(view, { [paramKey]: item.id }))
            }
            className="text-left text-body2 text-primary hover:underline"
          >
            {typeof item.name === "string" ? item.name : item.name.text}
          </button>
        ))}
      </div>
    ),
  });

  const accordionItems = [
    {
      title: "View All",
      titleClassName: "text-bold2",
      onClick: () => navigate(generateShopUrl("all")),
      startIcon: ShopBag,
    },
    ...(hasNewArrivals(products)
      ? [
          {
            title: "New Arrivals",
            titleClassName: "text-bold2",
            onClick: () => navigate(generateShopUrl("new-arrivals")),
            startIcon: ShopCart,
          },
        ]
      : []),
    ...(hasFeatured(products)
      ? [
          {
            title: "Featured",
            titleClassName: "text-bold2",
            onClick: () => navigate(generateShopUrl("featured")),
            startIcon: StarIcon,
          },
        ]
      : []),
    ...(hasBestSellers(products)
      ? [
          {
            title: "Best Sellers",
            titleClassName: "text-bold2",
            onClick: () => navigate(generateShopUrl("best-selling")),
            startIcon: StarIcon,
          },
        ]
      : []),
    ...(hasProducts(filteredBrands)
      ? [
          createExpandableSection(
            "Browse by Brand",
            filteredBrands,
            "browse-by-brand",
            "brands"
          ),
        ]
      : []),
    ...(hasProducts(filteredProductTypes)
      ? [
          createExpandableSection(
            "Browse by Category",
            filteredProductTypes,
            "browse-by-category",
            "productTypes"
          ),
        ]
      : []),
    ...(hasProducts(normalizedConcerns)
      ? [
          createExpandableSection(
            "Browse by Concern",
            normalizedConcerns,
            "browse-by-concern",
            "concerns"
          ),
        ]
      : []),
    ...(hasProducts(normalizedAreas)
      ? [
          createExpandableSection(
            "Browse by Area",
            normalizedAreas,
            "browse-by-area",
            "areas"
          ),
        ]
      : []),
  ];

  return (
    <div>
      <div className="flex flex-col">
        <ShopListHeader
          title="Products"
          subtitle="Shop our collection of bespoke products"
          bgGradient
          backgroundImageUrl={headerImage}
        />

        <AccordionList items={accordionItems} />

        <Outlet />
      </div>
    </div>
  );
};

export default ShopProductsPage;
